import { mdiMagnify } from '@mdi/js';
import Icon from '@mdi/react';
import { commonButtonMessages, fiduciarieMessages, FISCAL_CODE_MAX_LENGTH, validateDtaSearchFilters, VAT_CODE_MAX_LENGTH } from 'azshared';
import type { FiduciarieFieldErrors } from 'azshared';
import React, { FormEvent, useCallback, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { AppButton, AppButtonType, AppButtonVariant } from '../../../../components/buttons/app-button';
import { AppInputText, AppInputTextProps } from '../../../../components/input-text/app-input-text';
import { formatFiduciarieFieldError } from '../fiduciarie-field-error';
import { FiduciarieSearchFilters } from '../fiduciarie-types';
import './fiduciarie-search-filter.scss';

export interface FiduciarieSearchFilterProps {
  onSubmit: (params: FiduciarieSearchFilters) => void;
  onReset: () => void;
}

interface SearchField {
  key: keyof FiduciarieSearchFilters;
  label: MessageDescriptor;
  placeholder: MessageDescriptor;
  inputMode?: AppInputTextProps['inputMode'];
  maxLength?: number;
  uppercase?: boolean;
}

// One field for each scheme of the search API, in the order they are sent.
const searchFields: SearchField[] = [
  { key: 'nome', label: fiduciarieMessages.nameInputLabel, placeholder: fiduciarieMessages.nameInputPlaceholder },
  { key: 'cognome', label: fiduciarieMessages.surnameInputLabel, placeholder: fiduciarieMessages.surnameInputPlaceholder },
  {
    key: 'codiceFiscaleFiduciante',
    label: fiduciarieMessages.labelCodiceFiscaleFiduciaria,
    placeholder: fiduciarieMessages.actingPersonCodiceFiscaleInputPlaceholder,
    maxLength: FISCAL_CODE_MAX_LENGTH,
    uppercase: true
  },
  {
    key: 'ragioneSociale',
    label: fiduciarieMessages.ragioneSocialeInputLabel,
    placeholder: fiduciarieMessages.ragioneSocialeInputPlaceholder
  },
  {
    key: 'codiceClienteRappresentante',
    label: fiduciarieMessages.codiceClienteRappresentanteInputLabel,
    placeholder: fiduciarieMessages.codiceClienteRappresentanteInputPlaceholder
  },
  {
    key: 'codiceFiscale',
    label: fiduciarieMessages.labelCodiceFiscaleFiduciante,
    placeholder: fiduciarieMessages.codiceFiscaleInputPlaceholder,
    maxLength: FISCAL_CODE_MAX_LENGTH,
    uppercase: true
  },
  {
    key: 'addressBook',
    label: fiduciarieMessages.addressBookInputLabel,
    placeholder: fiduciarieMessages.addressBookInputPlaceholder
  },
  {
    key: 'partitaIva',
    label: fiduciarieMessages.partitaIvaInputLabel,
    placeholder: fiduciarieMessages.partitaIvaInputPlaceholder,
    inputMode: 'numeric',
    maxLength: VAT_CODE_MAX_LENGTH
  }
];

export const FiduciarieSearchFilter = (props: FiduciarieSearchFilterProps) => {
  const { onSubmit, onReset } = props;
  const { formatMessage } = useIntl();
  const [filters, setFilters] = useState<FiduciarieSearchFilters>({});
  const [errors, setErrors] = useState<FiduciarieFieldErrors>({});

  const handleChange = useCallback((field: SearchField, value?: string) => {
    setFilters((current) => ({ ...current, [field.key]: field.uppercase ? value?.toUpperCase() : value }));
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const validationErrors = validateDtaSearchFilters(filters);
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length) {
        return;
      }
      onSubmit(Object.fromEntries(searchFields.map(({ key }) => [key, filters[key]?.trim() || undefined])));
    },
    [onSubmit, filters]
  );

  const handleReset = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setFilters({});
      setErrors({});
      onReset();
    },
    [onReset]
  );

  return (
    <form className='fiduciarie-search-filter' onSubmit={handleSubmit} onReset={handleReset}>
      <div className='search-filter-title'>
        <Icon path={mdiMagnify} size={'1rem'} color={'#C4C4C4'} /> {formatMessage(fiduciarieMessages.widgetTitle)}
      </div>
      <div className='search-filter-grid'>
        {searchFields.map((field) => (
          <AppInputText
            key={field.key}
            value={filters[field.key] ?? ''}
            onChange={(value) => handleChange(field, value)}
            inputMode={field.inputMode}
            maxLength={field.maxLength}
            label={formatMessage(field.label)}
            placeholder={formatMessage(field.placeholder)}
            errorMessage={formatFiduciarieFieldError(formatMessage, errors[field.key])}
          />
        ))}
        <div className='search-filter-buttons'>
          <div className='buttons-wrapper'>
            <AppButton type={AppButtonType.RESET} variant={AppButtonVariant.Secondary} label={formatMessage(fiduciarieMessages.cleanFilters)} />
            <AppButton type={AppButtonType.SUBMIT} label={formatMessage(commonButtonMessages.research)} />
          </div>
        </div>
      </div>
    </form>
  );
};
